import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import Thumb from "@/components/Thumb";
import ColorSwatch from "@/components/ColorSwatch";
import { fetchPrimaryAttachments, bulkPrimaryMap, attachmentBlobUrl } from "@/lib/attachments";
import { X, CaretLeft, CaretRight } from "@phosphor-icons/react";

const FABRIC_CATEGORIES = ["Main Fabric", "Lining", "Lace", "Tulle", "Interlining", "Embroidery Fabric"];

function useList(url) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!url) return;
    api.get(url).then((r) => setRows(r.data)).catch((e) => {
      if (![403, 404].includes(e?.response?.status)) toast.error(formatApiError(e));
    });
  }, [url]);
  return rows;
}

function Lightbox({ items, index, onClose, onIndex }) {
  const [url, setUrl] = useState(null);
  const cur = items[index];
  useEffect(() => {
    if (cur?.attachment?.is_image) attachmentBlobUrl(cur.attachment.id).then(setUrl);
    else setUrl(null);
  }, [cur?.attachment?.id]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % items.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length]);
  if (!cur) return null;
  return (
    <div data-testid="fabric-lightbox" className="fixed inset-0 z-[60] flex items-center justify-center p-8"
      style={{ background: "rgba(20,20,20,0.92)" }} onClick={onClose}>
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-6 right-6 text-white/80 hover:text-white" aria-label="Close">
        <X size={22} weight="light" />
      </button>
      {items.length > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + items.length) % items.length); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3">
            <CaretLeft size={26} weight="light" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % items.length); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3">
            <CaretRight size={26} weight="light" />
          </button>
        </>
      )}
      <div className="max-h-full max-w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        {url ? <img src={url} alt={cur.material.name} className="max-h-[80vh] max-w-[85vw] object-contain" />
             : <div className="text-white/60 text-sm">Loading…</div>}
        <div className="text-white/85 text-xs font-mono flex items-center gap-4">
          <span>{cur.material.code} — {cur.material.name}</span>
          {items.length > 1 && <span className="text-white/50">{index + 1} / {items.length}</span>}
        </div>
      </div>
    </div>
  );
}

export default function FabricLibraryPage() {
  const { t, tEnum, localName, fmtCurrency } = useI18n();
  const materials = useList("/materials");
  const rolls = useList("/fabric-rolls");
  const suppliers = useList("/suppliers");
  const inv = useList("/inventory");
  const [primary, setPrimary] = useState({});
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // filters
  const [category, setCategory] = useState("");
  const [supplier, setSupplier] = useState("");
  const [composition, setComposition] = useState("");
  const [availability, setAvailability] = useState(""); // "" | "available" | "out"

  const fabrics = useMemo(
    () => materials.filter((m) => FABRIC_CATEGORIES.includes(m.category)),
    [materials]
  );

  useEffect(() => {
    if (fabrics.length) fetchPrimaryAttachments("material", fabrics.map((m) => m.id)).then((list) => setPrimary(bulkPrimaryMap(list)));
  }, [fabrics.length]);

  const supMap = Object.fromEntries(suppliers.map((s) => [s.id, s]));

  // Aggregate available meters + roll counts per material
  const stats = useMemo(() => {
    const map = {};
    for (const m of fabrics) map[m.id] = { available: 0, rolls: 0 };
    for (const r of rolls) {
      if (!map[r.material_id]) continue;
      map[r.material_id].available += Number(r.current_meters || 0) - Number(r.reserved_meters || 0);
      map[r.material_id].rolls += 1;
    }
    // Also add generic on_hand from ledger (fallback if no rolls)
    for (const row of inv) {
      const m = fabrics.find((x) => x.code === row.sku);
      if (m && map[m.id] && map[m.id].rolls === 0) map[m.id].available = row.on_hand;
    }
    return map;
  }, [fabrics, rolls, inv]);

  const compositions = useMemo(() => Array.from(new Set(fabrics.map((m) => m.composition).filter(Boolean))).sort(), [fabrics]);

  const filtered = fabrics.filter((m) => {
    if (category && m.category !== category) return false;
    if (supplier && m.supplier_id !== supplier) return false;
    if (composition && (m.composition || "") !== composition) return false;
    const av = stats[m.id]?.available || 0;
    if (availability === "available" && av <= 0) return false;
    if (availability === "out" && av > 0) return false;
    return true;
  });

  const galleryItems = filtered
    .map((m) => ({ material: m, attachment: primary[m.id] }))
    .filter((x) => x.attachment); // only items with images enter the lightbox

  const openLightbox = (m) => {
    const i = galleryItems.findIndex((x) => x.material.id === m.id);
    if (i >= 0) setLightboxIdx(i);
  };

  return (
    <div data-testid="fabric-library-page">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="overline mb-3">{t("library.eyebrow")}</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-none">{t("library.title")}</h1>
          <p className="mt-4 text-sm text-[#5C5A54] max-w-xl">{t("library.subtitle")}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="border p-4 mb-8 grid grid-cols-2 md:grid-cols-5 gap-4" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid="library-filters">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] mb-1">{t("common.category")}</div>
          <select data-testid="library-filter-category" className="w-full bg-transparent border-b py-1 text-sm outline-none"
            value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">{t("library.allCategories")}</option>
            {FABRIC_CATEGORIES.map((c) => <option key={c} value={c}>{tEnum("material.category", c)}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] mb-1">{t("library.composition")}</div>
          <select data-testid="library-filter-composition" className="w-full bg-transparent border-b py-1 text-sm outline-none"
            value={composition} onChange={(e) => setComposition(e.target.value)}>
            <option value="">{t("library.allCompositions")}</option>
            {compositions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] mb-1">{t("common.supplier")}</div>
          <select data-testid="library-filter-supplier" className="w-full bg-transparent border-b py-1 text-sm outline-none"
            value={supplier} onChange={(e) => setSupplier(e.target.value)}>
            <option value="">{t("library.allSuppliers")}</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] mb-1">{t("library.availability")}</div>
          <select data-testid="library-filter-availability" className="w-full bg-transparent border-b py-1 text-sm outline-none"
            value={availability} onChange={(e) => setAvailability(e.target.value)}>
            <option value="">{t("library.anyAvailability")}</option>
            <option value="available">{t("library.inStock")}</option>
            <option value="out">{t("library.outOfStock")}</option>
          </select>
        </label>
        <div className="flex items-end justify-end text-xs text-[#8F8D84] font-mono">
          <span data-testid="library-count">{filtered.length} / {fabrics.length}</span>
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="text-sm text-[#8F8D84] text-center py-16">{t("library.empty")}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-6" data-testid="library-grid">
          {filtered.map((m) => {
            const p = primary[m.id];
            const s = stats[m.id] || { available: 0, rolls: 0 };
            return (
              <div key={m.id} className="border flex flex-col overflow-hidden" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
                data-testid={`library-card-${m.code}`}>
                <button type="button" onClick={() => openLightbox(m)} className="aspect-square w-full bg-[#F4F3ED] overflow-hidden cursor-zoom-in"
                  aria-label={m.name}>
                  <Thumb attachment={p} size={400} alt={m.name} />
                </button>
                <div className="p-4 flex-1 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="overline flex-1">{m.code}</span>
                    {(m.color_hex || m.color_name) && <ColorSwatch hex={m.color_hex} name={m.color_name || m.color} size={16} />}
                  </div>
                  <div className="font-serif text-lg leading-tight tracking-tight">{localName(m)}</div>
                  <div className="text-xs text-[#5C5A54]">{m.color_name || m.color || "—"}</div>
                  {m.composition && <div className="text-xs text-[#8F8D84]">{m.composition}</div>}
                  <div className="text-xs text-[#8F8D84]">{supMap[m.supplier_id]?.name || "—"}</div>
                  <div className="mt-auto flex items-baseline justify-between pt-2 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
                    <span className="text-xs uppercase tracking-[0.15em]" style={{ color: s.available > 0 ? "var(--nylsu-gold)" : "var(--nylsu-warning)" }}>
                      {s.available.toFixed(1)} m
                    </span>
                    <span className="font-mono text-[10px] text-[#8F8D84]">{s.rolls} {t("library.rolls")}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lightboxIdx != null && (
        <Lightbox items={galleryItems} index={lightboxIdx} onClose={() => setLightboxIdx(null)} onIndex={setLightboxIdx} />
      )}
    </div>
  );
}
